"use client";

import * as React from "react";
import { CheckboxVisual } from "./checkbox";
import { cn } from "@/lib/utils";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { ChevronRightIcon } from "lucide-react";

function DropdownMenu({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />;
}

function DropdownMenuPortal({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Portal>) {
  return <DropdownMenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />;
}

function DropdownMenuTrigger({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
  return <DropdownMenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />;
}

function DropdownMenuContent({
  className,
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  const handleClick = (e: React.MouseEvent) => {
    // Prevent the dropdown from closing when clicking inside
    e.stopPropagation();
  };

  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        className={cn(
          "t-radix-dropdown bg-cede-bg-popover backdrop-blur-xl text-cede-text-primary border-cede-border-container z-overlay max-h-(--radix-dropdown-menu-content-available-height) min-w-32 overflow-x-hidden overflow-y-auto rounded-xl border p-2 shadow-lg",
          className,
        )}
        onClick={handleClick}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

function DropdownMenuGroup({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Group>) {
  return <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />;
}

function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  onSelect,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean;
  variant?: "default" | "destructive" | "highlight";
}) {
  const handleSelect = (event: Event) => {
    if (onSelect) {
      onSelect(event);
    }
  };

  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        "text-cede-text-primary hover:text-white focus:text-white hover:bg-cede-bg-component focus:bg-cede-bg-component",
        "data-[variant=destructive]:text-cede-error data-[variant=destructive]:hover:bg-cede-error/10 data-[variant=destructive]:focus:bg-cede-error/10 data-[variant=destructive]:hover:text-cede-error data-[variant=destructive]:focus:text-cede-error",
        "data-[variant=highlight]:bg-cede-primary/12 data-[variant=highlight]:text-cede-primary data-[variant=highlight]:font-semibold data-[variant=highlight]:hover:bg-cede-primary/18 data-[variant=highlight]:hover:text-white data-[variant=highlight]:focus:bg-cede-primary/18 data-[variant=highlight]:focus:text-white",
        "[&_svg:not([class*='text-'])]:text-cede-text-tertiary relative flex cursor-pointer items-center gap-2 rounded-button px-3 py-2.5 body1 outline-none select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-cede-primary",
        className,
      )}
      onSelect={handleSelect}
      {...props}
    />
  );
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  onCheckedChange,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
  const handleCheckedChange = (checked: boolean) => {
    if (onCheckedChange) {
      onCheckedChange(checked);
    }
  };

  const handleSelect = (event: Event) => {
    // Prevent the dropdown from closing when selecting checkbox items
    event.preventDefault();
  };

  return (
    <DropdownMenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        "relative flex cursor-pointer items-center gap-2 rounded-button py-2.5 pr-3 pl-9 body1",
        "text-cede-text-primary hover:text-white focus:text-white",
        "hover:bg-cede-bg-component focus:bg-cede-bg-component",
        "transition-colors duration-200",
        "outline-none select-none",
        "data-disabled:pointer-events-none data-disabled:opacity-50",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "focus-visible:ring-2 focus-visible:ring-cede-primary",
        className,
      )}
      checked={checked}
      onCheckedChange={handleCheckedChange}
      onSelect={handleSelect}
      {...props}
    >
      <span className="pointer-events-none absolute left-2.5 flex size-4 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator asChild>
          <CheckboxVisual checked={checked} />
        </DropdownMenuPrimitive.ItemIndicator>
        {!checked && <CheckboxVisual checked={false} />}
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
}

function DropdownMenuRadioGroup({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>) {
  return <DropdownMenuPrimitive.RadioGroup data-slot="dropdown-menu-radio-group" {...props} />;
}

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) {
  return (
    <DropdownMenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(
        "text-cede-text-primary hover:text-white focus:text-white hover:bg-cede-bg-component focus:bg-cede-bg-component relative flex cursor-pointer items-center gap-2 rounded-button py-2.5 pr-3 pl-9 body1 outline-none select-none data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-cede-primary",
        className,
      )}
      {...props}
    >
      <span className="pointer-events-none absolute left-2.5 flex size-4 items-center justify-center">
        <div
          className={cn(
            "size-4 rounded-full border-2 flex items-center justify-center transition-all duration-200",
            "border-cede-primary/50 bg-transparent hover:border-cede-primary/80",
          )}
        >
          <DropdownMenuPrimitive.ItemIndicator>
            <div className="size-2 rounded-full bg-cede-primary" />
          </DropdownMenuPrimitive.ItemIndicator>
        </div>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
}

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean;
}) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn("px-2 py-1.5 text-sm font-medium text-cede-text-primary data-[inset]:pl-8", className)}
      {...props}
    />
  );
}

function DropdownMenuSeparator({ className, ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn("bg-cede-border-container -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

function DropdownMenuShortcut({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn("text-cede-text-tertiary ml-auto text-xs tracking-widest", className)}
      {...props}
    />
  );
}

function DropdownMenuSub({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>) {
  return <DropdownMenuPrimitive.Sub data-slot="dropdown-menu-sub" {...props} />;
}

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
  inset?: boolean;
}) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        "text-cede-text-primary hover:text-white focus:text-white hover:bg-cede-bg-component focus:bg-cede-bg-component data-[state=open]:bg-cede-bg-component data-[state=open]:text-white flex cursor-pointer items-center rounded-button px-3 py-2.5 body1 outline-none select-none data-[inset]:pl-8 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-cede-primary",
        className,
      )}
      {...props}
    >
      {children}
      {<ChevronRightIcon className="ml-auto size-4" />}
    </DropdownMenuPrimitive.SubTrigger>
  );
}

function DropdownMenuSubContent({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
  return (
    <DropdownMenuPrimitive.SubContent
      data-slot="dropdown-menu-sub-content"
      className={cn(
        "t-radix-dropdown bg-cede-bg-popover backdrop-blur-xl text-cede-text-primary border-cede-border-container z-overlay min-w-32 overflow-hidden rounded-xl border p-2 shadow-lg",
        className,
      )}
      {...props}
    />
  );
}

export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
};
